/*
 * This code is protected by intellectual property rights.
 * Dr. Ing. h.c. F. Porsche AG owns exclusive rights of use.
 * © 2017-2026, Dr. Ing. h.c. F. Porsche AG.
 */

import { Injectable, inject } from '@angular/core'
import { AppConfig, HeaderExternalLink, LocaleInformation } from './config.interface'
import { environment } from '../../../environments/environment'
import { HttpInterceptorConfig } from '@auth0/auth0-angular'
import { Observable, of, ReplaySubject, take } from 'rxjs'
import { WINDOW } from '../utils'
import { tap } from 'rxjs/operators'
import { ConfigLoaderService } from './config-loader.service'

@Injectable({
  providedIn: 'root',
})
export class ConfigService {
  private configLoader = inject(ConfigLoaderService)
  private _window = inject<Window>(WINDOW)

  protected config: AppConfig = {}
  protected country = ''
  protected locale = ''
  protected _initialized = new ReplaySubject<void>(1)
  initialized = this._initialized.asObservable().pipe(take(1))

  init(locale: string, country: string): Observable<AppConfig> {
    this.locale = locale
    this.country = country

    return this.loadConfig(locale, country).pipe(
      tap(() => {
        this._initialized.next()
      }),
    )
  }

  setUrlLocaleCountryInformation(locale: string, country: string) {
    this.country = country
    this.locale = locale
  }

  getUrlLocale(): string {
    return this.locale
  }

  getUrlCountry(): string {
    return this.country
  }

  getTranslationFileName(): string {
    return this.config?.localeInformation?.translationFileName as string
  }

  getFallbackTranslationFileName(): string {
    return this.config?.localeInformation?.fallbackTranslationFileName as string
  }

  getAngularLocale(): string {
    return this.config?.localeInformation?.angularLocale as string
  }

  getIso6391Language(): string {
    return this.config?.localeInformation?.iso6391Language as string
  }

  getLocaleInformation(): LocaleInformation {
    return this.config?.localeInformation as LocaleInformation
  }

  getInvalidLocaleRedirectUrl(): string {
    return this.config?.redirectInformation?.url as string
  }

  isCNDomain(): boolean {
    return this.getHostname().includes('.cn')
  }

  getHostname(): string {
    return this._window.location.hostname
  }

  getBaseUrl(): string {
    return environment.urls.ui
  }

  getBaseApiUrl(): string {
    return environment.urls.api
  }

  getBaseShortLinkApiUrl(): string {
    return environment.urls.shortLinkApi
  }

  getExternalUrl(key: string, anchor: string | null = null): string {
    // eslint-disable-next-line no-prototype-builtins
    if (environment.externalUrls.hasOwnProperty(key)) {
      let externalUrl = environment.externalUrls[key as keyof typeof environment.externalUrls]
        .replace(':country', this.getUrlCountry())
        .replace(':locale', this.getUrlLocale())
      if (anchor) {
        externalUrl = `${externalUrl}-${anchor}`
      }
      return externalUrl
    }

    return null!
  }

  getLocalisationBaseUrl(): string {
    return this.isCNDomain() ? environment.urls.localisationCN : environment.urls.localisation
  }

  get getTranslationsUrl(): string {
    const STAGE = environment.production ? 'RELEASE' : 'LATEST'
    return `${this.getLocalisationBaseUrl()}translations/${STAGE}/signup/`
  }

  getHeaderLinks(): HeaderExternalLink[] {
    return this.config?.NAVIGATION as HeaderExternalLink[]
  }

  get apiKeyWhiteListUrls(): string | (string | RegExp)[] {
    return environment.apiKeyWhiteListUrls
  }

  isProduction(): boolean {
    return environment.production
  }

  getGTMApplicationID(): string {
    return environment.googleTagManagerV2.data.context.applicationId
  }

  getGTMEnvironment(): string {
    return environment.googleTagManagerV2.data.context.environment
  }

  getGTMIsEnabled(): boolean {
    return environment.googleTagManagerV2.enable && !this.config.isTestAccount
  }

  getGTMContainerId(): string {
    return environment.googleTagManagerV2.containerId
  }

  getHttpInterceptorConfig(): HttpInterceptorConfig {
    return environment?.oauth?.httpInterceptor
  }

  isRegisterWithMobileNumberAllowed(): boolean {
    return environment.features.registerWithMobileNumberCountries.includes(this.getUrlCountry().toLowerCase())
  }

  private loadConfig(locale: string, country: string): Observable<AppConfig> {
    if (this.configLoader !== null) {
      return this.configLoader.getConfig(locale, country).pipe(
        tap((config: AppConfig) => {
          this.config = config
        }),
      )
    }

    return of(null as unknown as AppConfig)
  }
}
